import { useNavigate, useParams } from "react-router-dom";
import styles from "./SubscriptionPlan.module.scss";
import { ISubscriptionPlanModel } from "./models/subscriptionPlanModel";
import { useApiQuery } from "../../../../common/infrastructure/hooks/useApiQuery";
import { endpointsMetadata } from "../../../../common/infrastructure/routes/endpointsMetadata";
import { useAddAdditionalLayoutButton } from "../../../../common/infrastructure/hooks/useAddAdditionalLayoutButton";
import { IconType } from "../../../../common/icons/Icon";
import {
  constructEmployeeHref,
  constructFamilyHref,
  constructUserHref,
  constructUserPlanEditHref,
  routePaths,
} from "../../../../common/infrastructure/routes/routePaths";
import { SpecificPageNotFound } from "../../../../common/components/errorPages/SpecificPageNotFound";
import { dateUtility } from "../../../../common/infrastructure/utilities/dateUtility";
import { PaymentTypes, PlanConsumerType } from "../../../../common/enums";
import { DefaultSection } from "../../../../common/components/common/DefaultSection";
import { SkeletonItem } from "../../../../common/components/skeletons/SkeletonItem";
import { SectionInfoRow } from "../../../../common/components/common/SectionInfoRow";
import { PaymentTypesLabels } from "../../../../common/labels";
import { RenderWhen } from "../../../../common/infrastructure/components/RenderWhen";
import { ItemsList } from "../../../../common/components/itemsList/ItemsList";
import { EventHistoryItem } from "../../../../common/components/common/eventHistoryItem/EventHistoryItem";
import { PriceDisplay } from "../../../../common/components/PriceDisplay/PriceDisplay";
import { useAppContext } from "../../../../AppContext";
import { studioHidesPlans } from "../../../../common/infrastructure/helpers/studioHelper";

export const SubscriptionPlan = () => {
  const { userPlanId } = useParams();
  const navigate = useNavigate();

  const { data: plan, isLoading } = useApiQuery<ISubscriptionPlanModel>({
    queryKey: [endpointsMetadata.user.getUserPlan, userPlanId],
    endpoint: `${endpointsMetadata.user.getUserPlan}?userPlanId=${userPlanId}`,
    enabled: !!userPlanId,
    globalIsLoading: false,
  });

  const { selectedStudio } = useAppContext();

  // The page stays open for the reports and the dashboard, but editing a sold card goes
  // through "Зареждане на карта", which is hidden with the plans.
  useAddAdditionalLayoutButton({
    iconType: IconType.EditIcon,
    href: constructUserPlanEditHref(Number(userPlanId)),
    rawPath: routePaths.userPlanEdit,
    validator: !studioHidesPlans(selectedStudio),
  });

  const backToUsers = () => {
    navigate(routePaths.users);
  };

  if (!plan && !isLoading) {
    return (
      <SpecificPageNotFound
        text={"За съжаление няма такъв абонамент."}
        buttonText={"Обратно към списъка с потребители"}
        onButtonClick={backToUsers}
      />
    );
  }

  const validityText = `${dateUtility.formatDate(
    plan?.startedOn
  )} - ${dateUtility.formatDate(plan?.validUntil)}`;

  const remainingVisitsText = `${plan?.remainingVisits}/${plan?.initialVisits}`;

  // A plan that has used up all of its visits is "completed" (приключен), as
  // opposed to "expired" (изтекъл) when its validity period simply ran out.
  // Unlimited plans (remainingVisits == null) can only ever expire by date.
  const remainingVisits = plan?.remainingVisits;
  const isExhausted = remainingVisits != null && remainingVisits <= 0;

  const statusText = plan?.isActive
    ? "Активен"
    : plan?.isDeleted
    ? "Изтрит"
    : isExhausted
    ? "Приключен"
    : "Изтекъл";

  const consumatorHref =
    plan?.plan.consumerType === PlanConsumerType.Family
      ? constructFamilyHref(plan?.user.id ?? 0)
      : constructUserHref(plan?.user.id ?? 0);

  // Payment types that should show currency (types where we receive money directly)
  // The currency is no longer appended here — the amount itself carries it.
  const paymentTypeLabel =
    PaymentTypesLabels[plan?.paymentType ?? PaymentTypes.Card];

  return (
    <div className={styles.root}>
      <DefaultSection>
        <div className={styles.root_title}>
          <SkeletonItem isLoading={isLoading} value={plan?.plan.name} />
        </div>
        <div className={styles.root_subtitle}>
          <SkeletonItem
            isLoading={isLoading}
            value={`#${plan?.plan?.id}, ${plan?.plan.type}`}
            width={100}
          />
        </div>
      </DefaultSection>

      <DefaultSection className={styles.root_info_section}>
        <SectionInfoRow
          label={"Продаден от служител"}
          data={plan?.createdBy.name}
          isLoading={isLoading}
          skekletonWidth={300}
          onClick={() =>
            navigate(constructEmployeeHref(plan?.createdBy.id ?? 0))
          }
        />
        <SectionInfoRow
          label={"Продаден на"}
          data={dateUtility.formatDate(plan?.createdOn)}
          isLoading={isLoading}
          skekletonWidth={240}
        />
        <SectionInfoRow
          label={"Платена сума"}
          data={
            <PriceDisplay
              minPrice={plan?.pricePaid ?? 0}
              currencyId={plan?.currencyId}
            />
          }
          isLoading={isLoading}
          skekletonWidth={240}
        />
        <SectionInfoRow
          label={"Начин на плащане"}
          data={paymentTypeLabel}
          isLoading={isLoading}
          skekletonWidth={240}
        />
      </DefaultSection>

      <DefaultSection className={styles.root_info_section}>
        <SectionInfoRow
          label={"Потребител"}
          data={plan?.user.name}
          isLoading={isLoading}
          skekletonWidth={240}
          onClick={() => navigate(consumatorHref)}
        />
        <SectionInfoRow
          label={"Номер на абонаментa"}
          data={plan?.userPlanId}
          isLoading={isLoading}
          skekletonWidth={240}
        />
        <SectionInfoRow
          label={"Дати на валидност"}
          data={validityText}
          isLoading={isLoading}
          skekletonWidth={240}
        />
        <SectionInfoRow
          label={"Оставащи посещения"}
          data={remainingVisitsText}
          isLoading={isLoading}
          skekletonWidth={240}
        />
        <SectionInfoRow
          label={"Статус"}
          data={statusText}
          isLoading={isLoading}
          skekletonWidth={240}
        />

        <RenderWhen condition={!!plan?.deletedOn}>
          <SectionInfoRow
            label={"Изтрит на"}
            data={dateUtility.formatDate(plan?.deletedOn)}
            isLoading={isLoading}
            skekletonWidth={240}
          />
        </RenderWhen>
      </DefaultSection>

      <ItemsList
        isLoading={isLoading}
        noItemsText="Не беше открита история."
        className={styles.list}
        title="История"
        itemTemplate={(item, index, actionButtons) => {
          return (
            <EventHistoryItem
              event={item}
              key={item?.id ?? index}
              isLoading={isLoading}
              actionButtons={actionButtons}
            />
          );
        }}
        items={plan?.history ?? []}
        fullHeight
      />
    </div>
  );
};
